/**
 * AdminGpInbound — Iter 81.
 *
 * Lists inbound referrals submitted via /refer/{code} and lets staff move
 * each one through the workflow:
 *   new → acknowledged → scheduled (terminal)
 *                       → declined  (terminal)
 *
 * Each transition fires an auto-reply e-mail to the GP via Resend
 * (best-effort; logged but never blocks the state change).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox, CheckCircle2, CalendarClock, XOctagon, Loader2, Mail, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useAuth } from '../../lib/auth';

const STATUS_BADGE = {
  new:          'bg-[#FFF6E2] text-[#7A5500] border-[#FFB000]/40',
  acknowledged: 'bg-[#E3F2FD] text-[#003D80] border-[#003D80]/30',
  scheduled:    'bg-[#E8F5E9] text-[#1B5E20] border-[#1B5E20]/30',
  declined:     'bg-[#FFEBEE] text-[#B71C1C] border-[#B71C1C]/30',
};

export default function AdminGpInbound() {
  const { locale } = useAuth();
  const [params, setParams] = useSearchParams();
  const filterGp = params.get('gp') || '';
  const [statusFilter, setStatusFilter] = useState(params.get('status') || 'all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/gp/referrals/inbound')
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const filtered = rows.filter((r) => {
    if (filterGp && (r.gp_email || '').toLowerCase() !== filterGp.toLowerCase()) return false;
    if (statusFilter !== 'all' && (r.status || 'new') !== statusFilter) return false;
    return true;
  });

  const transition = async (ref, status, extra = {}) => {
    setBusyId(ref.referral_id);
    try {
      const body = { status, ...extra };
      const { data } = await api.patch(
        `/gp/referrals/inbound/${ref.referral_id}/status`, body,
      );
      setRows((prev) => prev.map((r) => r.referral_id === data.referral_id ? data : r));
      toast.success(`Marked ${status}. ${ref.gp_email ? 'Auto-reply emailed.' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl" data-testid="gp-inbound">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">{t(locale, 'referral_inbox')}</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight flex items-center gap-3">
          <Inbox className="w-7 h-7 text-brand"/> {t(locale, 'inbound_gp_referrals')}
        </h1>
        <p className="text-fg-muted text-sm mt-1 max-w-2xl">
          {t(locale, 'inbound_lede')}
        </p>
      </div>

      {/* Filters */}
      <div className="bento-card flex flex-wrap items-center gap-3 mb-4" data-testid="gp-inbound-filters">
        <div className="text-xs text-fg-muted flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5"/> {t(locale, 'filter')}
        </div>
        <select className="input max-w-[180px]" value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  const p = new URLSearchParams(params);
                  if (e.target.value === 'all') p.delete('status'); else p.set('status', e.target.value);
                  setParams(p);
                }}
                data-testid="gp-inbound-status-filter">
          <option value="all">{t(locale, 'all_statuses')}</option>
          <option value="new">{t(locale, 'status_new')}</option>
          <option value="acknowledged">{t(locale, 'status_acknowledged')}</option>
          <option value="scheduled">{t(locale, 'status_scheduled')}</option>
          <option value="declined">{t(locale, 'status_declined')}</option>
        </select>
        {filterGp && (
          <button onClick={() => { const p = new URLSearchParams(params); p.delete('gp'); setParams(p); }}
                  className="text-xs text-brand underline" data-testid="gp-inbound-clear-gp">
            {t(locale, 'clear_gp_filter')} ({filterGp})
          </button>
        )}
        <div className="ml-auto text-xs text-fg-muted">
          {t(locale, 'showing_x_of_y', { x: filtered.length, y: rows.length })}
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex items-center gap-2 text-fg-muted text-sm" data-testid="gp-inbound-loading">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading referrals…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bento-card text-center py-12" data-testid="gp-inbound-empty">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-fg-muted"/>
          <p className="text-sm text-fg-muted">{t(locale, 'no_referrals_match')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const status = r.status || 'new';
            const isTerminal = status === 'scheduled' || status === 'declined';
            return (
              <div key={r.referral_id} className="bento-card" data-testid={`gp-inbound-row-${r.referral_id}`}>
                <div className="flex flex-wrap items-start gap-3 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-base">{r.patient_name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider ${STATUS_BADGE[status]}`}
                            data-testid={`gp-inbound-status-${r.referral_id}`}>
                        {status}
                      </span>
                      <span className="text-[11px] text-fg-muted">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-fg-muted mb-2">
                      {t(locale, 'referred_by')} <strong>{r.gp_name || r.gp_email}</strong>
                      {' · '}{r.discipline || '—'} · {t(locale, 'urgency').toLowerCase()} {r.urgency || 'flexible'}
                    </div>
                    {r.presenting_complaint && (
                      <div className="text-sm">{r.presenting_complaint}</div>
                    )}
                    {(r.patient_email || r.patient_phone) && (
                      <div className="text-[11px] text-fg-muted mt-1">
                        {r.patient_email && <>📧 {r.patient_email} </>}
                        {r.patient_phone && <>📞 {r.patient_phone}</>}
                      </div>
                    )}
                    {r.note && <div className="text-[11px] text-fg-muted mt-1">Note: {r.note}</div>}
                  </div>

                  {!isTerminal && (
                    <div className="flex flex-wrap gap-2">
                      {status === 'new' && (
                        <button onClick={() => transition(r, 'acknowledged')}
                                disabled={busyId === r.referral_id}
                                className="btn-secondary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                                data-testid={`gp-inbound-ack-${r.referral_id}`}>
                          {busyId === r.referral_id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin"/>
                            : <Mail className="w-3.5 h-3.5"/>}
                          {t(locale, 'acknowledge')}
                        </button>
                      )}
                      <button onClick={() => {
                                  const when = window.prompt('Scheduled when? (ISO 8601, e.g. 2026-02-20T10:00)', '');
                                  if (when) transition(r, 'scheduled', { schedule_when: when });
                                }}
                              disabled={busyId === r.referral_id}
                              className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                              data-testid={`gp-inbound-schedule-${r.referral_id}`}>
                        {busyId === r.referral_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin"/>
                          : <CalendarClock className="w-3.5 h-3.5"/>}
                        {t(locale, 'schedule')}
                      </button>
                      <button onClick={() => {
                                  const reason = window.prompt('Decline reason (sent to GP)', '');
                                  if (reason !== null) transition(r, 'declined', { decline_reason: reason });
                                }}
                              disabled={busyId === r.referral_id}
                              className="btn-secondary inline-flex items-center gap-1.5 text-xs disabled:opacity-50 text-[#B71C1C]"
                              data-testid={`gp-inbound-decline-${r.referral_id}`}>
                        <XOctagon className="w-3.5 h-3.5"/> {t(locale, 'decline')}
                      </button>
                    </div>
                  )}
                  {status === 'scheduled' && r.scheduled_when && (
                    <div className="text-xs text-[#1B5E20] inline-flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4"/> {r.scheduled_when}
                    </div>
                  )}
                </div>
                {(r.status_history || []).length > 0 && (
                  <details className="mt-3 text-[11px] text-fg-muted">
                    <summary className="cursor-pointer hover:text-fg">
                      Audit ({r.status_history.length})
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {r.status_history.map((h, i) => (
                        <li key={i} className="font-mono">
                          {h.at}: <strong>{h.from}</strong> → <strong>{h.to}</strong> by {h.actor}
                          {h.decline_reason && ` · reason: ${h.decline_reason}`}
                          {h.schedule_when && ` · when: ${h.schedule_when}`}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
